import React, {useEffect, useState} from "react";
import type { ContextProps } from '../../types';
import {PageTitle} from "../../Components/Landmarks";
import {LayoutFullBleed} from "../../Components/Layout";
import {Button, CheckBoxField, inputStyles, TextField} from "../../Components/Inputs";
import {GridCell, gridConstants, GridContainer} from "../../Components/Grid";
import {libraryCatalogApi} from "./libraryCatalogApi";
import {useInput} from "../../Components/Inputs/inputUtility";
import {LinkButton} from "../../Components/Link";
import {locations} from "../../Utilities/Location";
import {Allow, policyEvents} from "../../Components/Authorize";
import {isArrayNullOrEmpty} from "../../Utilities/Types/arrayUtilities";
import {libraryQueueApi} from "./libraryQueueApi";
import {isObjectNullOrEmpty} from "../../Utilities/Types/objectUtilities";
import {isAdmin, isFimcAdmin} from "../../Utilities/LocalStorage/storageUtilities";
import {LibraryCatalogView} from "./LibrarayCatalogView";
import {SYSTEM_TYPES} from "./libraryConstants";

export const LibraryCatalogContainer = ({
                                            handleApiCall,
                                            handleSetLibrarySearchCriteria,
                                            librarySearchCriteria,
                                            redirect,
                                            systemType,
                                            systemTypeText,
                                        }: ContextProps) => {
    const [resources, setResources] = useState<Record<string, unknown>[]>([]);

    const [totalRecords, setTotalRecords] = useState(0);

    const keywordSearch = useInput("");
    const barCodeSearch = useInput("");
    const [includeInactive, setIncludeInactive] = useState(false);

    const handleEdit = (catalogId: string, catalogFormatId?: string) => {
        const path = catalogFormatId
            ? locations.LIBRARY_FORMAT.getLink([catalogId, catalogFormatId]).to
            : locations.LIBRARY_ITEM.getLink([catalogId, systemType]).to;
        redirect(path);
    }

    const handleOrderRequest = (catalogId: string, catalogFormatId: string) => {
        redirect(locations.ORDER_REQUEST.getLink([catalogId, catalogFormatId]).to);
    }

    const loadResourcesResult = (result: { catalogViewModels: Record<string, unknown>[]; totalRecords: number }) => {
        setResources(result.catalogViewModels);
        setTotalRecords(result.totalRecords);
    }

    const handleAddToQueue = async (catalogFormatId: string) => {
        handleApiCall(() => libraryQueueApi.addToQueue(catalogFormatId), getResources);
    }

    const getResources = () => {
        if (isObjectNullOrEmpty(librarySearchCriteria)) return;

        const updatedLibrarySearchCriteria = {
            ...librarySearchCriteria,
            searchKeyword: keywordSearch.value,
            includeInactive,
        };

        handleApiCall(() => libraryCatalogApi.getResources(updatedLibrarySearchCriteria), (results: { catalogViewModels: Record<string, unknown>[]; totalRecords: number }) => loadResourcesResult(results));
    }

    const handleUpdateSystemType = () => {
        if (isObjectNullOrEmpty(librarySearchCriteria)) return;
        const updatedLibrarySearchCriteria = {...librarySearchCriteria};
        updatedLibrarySearchCriteria?.Search({systemType: systemType});
        handleSetLibrarySearchCriteria(updatedLibrarySearchCriteria);
    };

    const handleSearch = () => {
        if (isObjectNullOrEmpty(librarySearchCriteria)) return;

        const updatedLibrarySearchCriteria = {
            ...librarySearchCriteria,
            searchKeyword: keywordSearch.value,
            barCode: barCodeSearch.value,
            includeInactive,
        };

        handleApiCall(() => libraryCatalogApi.getResources(updatedLibrarySearchCriteria), (results: { catalogViewModels: Record<string, unknown>[]; totalRecords: number }) => loadResourcesResult(results));
    }

    useEffect(() => {
        if (!librarySearchCriteria) return;

        if (systemType === librarySearchCriteria.systemType && librarySearchCriteria.loadSearchResults)
            getResources();
        else
            handleUpdateSystemType();

    }, [librarySearchCriteria, systemType, keywordSearch.value, includeInactive]);

    if (!librarySearchCriteria)
        return null;

    return (
        <>
            <PageTitle h1={systemTypeText}/>           
              
            <div className={`searchBox margin--bottom`}>
                <div className={`searchBox__title`}>Search the catalog</div>
                <GridContainer hasMarginX hasMarginY className={`searchBox__input`}>
                    <TextField
                        labelClass={`show-for-sr`}
                        name={`txtSearch`}
                        label={`keyword Search box`}
                        placeholder={`Keyword`}
                        large_columns={gridConstants.column.TEN}
                        medium_columns={gridConstants.column.EIGHT}
                        {...keywordSearch}
                    />
                    {
                        isAdmin() &&
                        <>
                            <TextField
                                labelClass={`show-for-sr`}
                                name={`barCodeSearch`}
                                label={`barCode Search box`}
                                placeholder={`Access Number`}
                                large_columns={gridConstants.column.TEN}
                                medium_columns={gridConstants.column.EIGHT}
                                {...barCodeSearch}
                            />
                        </>
                    }

                    <GridCell large_columns={gridConstants.column.TWO} medium_columns={gridConstants.column.FOUR}>
                        <Button
                            buttonType={inputStyles.button.types.SEARCH}
                            label={`Search`}
                            name={`btnSearch`}
                            onClick={handleSearch}
                            isPrimary
                        />
                    </GridCell>
                </GridContainer>
                {
                    isAdmin() &&
                    <GridContainer hasMarginX>
                        <GridCell>
                            <CheckBoxField
                                name="includeInactive"
                                label="Include inactive items"
                                checked={includeInactive}
                                onChange={(event) => setIncludeInactive(event.target.checked)}
                            />
                        </GridCell>
                    </GridContainer>
                }
            </div>

            <GridContainer>
                <GridCell className={`text-center`}>
                    <LinkButton
                        {...locations.LIBRARY_WHATS_NEW.getLink(systemType)}
                    />
                    <LinkButton {...locations.LIBRARY_QUEUE.getLink(systemType)} />
                    <LinkButton {...locations.LOAN_HISTORY.getLink(systemType)} />

                    <Allow policyEvent={policyEvents.MANAGE_CATALOG}>
                        <LinkButton
                            {...locations.LIBRARY_ITEM.getLink([undefined, systemType])}
                            label={`Add New Item`}
                        />
                        <LinkButton
                            {...locations.LIBRARY_PUBLISHER.getLink([
                                undefined,
                                systemType,
                            ])}
                        />
                        <LinkButton
                            {...locations.LIBRARY_VENDOR.getLink([undefined, systemType])}
                        />
                        {
                            isFimcAdmin() && (
                                <LinkButton
                            {...locations.LIBRARY_SHIPPING_LOCATIONS.getLink([undefined, systemType])}
                        /> 
                        )}
                    </Allow>
                </GridCell>
            </GridContainer>

            <LayoutFullBleed className={`catalog`}>
                <header>
                    <h2>Search Results</h2>
                </header>
                {
                    resources && !isArrayNullOrEmpty(resources) &&
                    <LibraryCatalogView
                        resources={resources}
                        handleAddToQueue={handleAddToQueue}
                        handleEdit={handleEdit}
                        handleOrderRequest={handleOrderRequest}
                        handleSetLibrarySearchCriteria={handleSetLibrarySearchCriteria}
                        librarySearchCriteria={librarySearchCriteria}
                        totalRecords={totalRecords}
                    />

                }
                {
                    isArrayNullOrEmpty(resources) &&
                    <>
                        <GridCell className={`text-center margin--topNone margin--bottomNone`}>
                            <p className={`text-center`}>No results.</p>
                            {
                                systemType === SYSTEM_TYPES.Textbooks.toString() &&
                                <LinkButton
                                    {...locations.ORDER_REQUEST.link}
                                    label={`Special Order Request`}
                                />
                            }
                        </GridCell>
                    </>
                }
            </LayoutFullBleed>
        </>
    );
};

